import { Tooltip } from '@nextui-org/react';
import { Editor } from 'draft-js';
import { motion } from 'framer-motion';
import styled from 'styled-components';

import { BackArrowIcon, ChatIcon, CSVFileIcon } from '../../assets';
import { Chevron2Icon } from '../../assets';
import { VehicleChatPlaceholder } from '../../assets';
import FileIcon from '../../assets/FileIcon';
import PDFFileIcon from '../../assets/PDFFileIcon';
import ZIndexLayers from '../../services/ZIndexLayers';
import { ThemeProp } from '../../theme/Themes';
import Avatar, { AvatarVariant } from '../Avatar';
import Button from '../Button';
import IconButton from '../Button/IconButton';
import ChatMessageBubble from '../ChatMessageBubble';
import InfinitelyScrolled from '../InfinitelyScrolled';
import { ShimmerText } from '../Shimmer';
import Text from '../Text';

const borderRadius = 'border-radius: 0.5rem 0.5rem 0rem 0rem';
interface ContainerProps {
  $isFloatingWindow?: boolean;
}

export const Container = styled.div<ContainerProps>`
  display: flex;
  position: relative;
  flex-direction: column;
  height: ${(props: ContainerProps) => (props.$isFloatingWindow ? '90vh' : '100%')};
  overflow: hidden;
  background: ${(props: ThemeProp) => props.theme.colors.layout.surface.default};
  ${(props: ContainerProps) => props.$isFloatingWindow && borderRadius};

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    height: 100%;
    border-radius: 0rem;
  }
`;

export const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0 1rem;
  min-height: 3.5rem;
  ${borderRadius};
  border-bottom: ${(props: { $isExpanded: any; theme: { colors: { greyscale: { c800: any } } } }) =>
    props.$isExpanded ? 'none' : `1px solid ${props.theme.colors.greyscale.c800}`};

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    border-radius: 0rem;
  }
`;

export const ChatHeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 1rem;
`;

export const ChatHeaderLeftContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  overflow: hidden;
`;

export const ChatHeaderTitle = styled.span`
  color: ${(props: ThemeProp) => props.theme.colors.content.default};
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.lineHeight};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    font-size: ${(props: ThemeProp) => props.theme.fontSize.mlg};
    line-height: ${(props: ThemeProp) => props.theme.lineHeight.xs};
  }
`;

export const ExpandedChatHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 2.1rem;
  padding-left: 1.438rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid ${(props: ThemeProp) => props.theme.colors.greyscale.c800};
`;

export const ExpandedChatHeaderMembers = styled.div`
  display: flex;
  margin-right: 0.563rem;
`;

export const ExpandedChatHeaderMember = styled(Avatar)`
  width: 1.25rem;
  min-width: 1.25rem;
  height: 1.25rem;
  min-height: 1.25rem;
  border-radius: 2.13rem;

  // skips the first one
  :nth-of-type(n + 2) {
    margin-left: -0.25rem;
  }

  .nextui-avatar-text {
    -webkit-text-stroke: 0;
    font-size: 0.75rem;
    color: ${(props: ThemeProp) => props.theme.colors.greyscale.c800};
  }

  .nextui-avatar-bg {
    opacity: 1;
    background: ${(props: ThemeProp) => props.theme.colors.primary.c300};
  }
`;

export const ExpandedChatHeaderAllMembers = styled(ExpandedChatHeaderMember).attrs((props: ThemeProp) => {
  return {
    fontColor: props.theme.colors.primary.c700,
  };
})`
  width: 2rem;
  height: 2rem;
  margin-bottom: 0.375rem;
  .nextui-avatar-text {
    -webkit-text-stroke: 0;
    font-size: 0.75rem;
  }
`;

export const ExpandedChatHeaderMembersCount = styled(Text)`
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.fontSize};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.lineHeight};
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
  cursor: pointer;
`;

export const ExpandedChatHeaderOnlineMembersCount = styled(Text)`
  margin-left: 0.25rem;
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.fontSize};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.lineHeight};
  color: ${(props: ThemeProp) => props.theme.colors.system.success};
`;

export const VehicleChatIconContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  position: relative;
  min-width: 4.2rem;
  max-width: 4.2rem;
`; // Includes vehicle image + (user image + status)

export const VehicleIcon = styled.img`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 2.5rem;
  background-color: lightgray;
  object-fit: cover;
`;
export const MemberIconContainer = styled.div`
  display: flex;
  position: absolute;
  left: 2rem;
`; // Includes user image + status

export const MemberIcon = styled(Avatar).attrs(() => ({
  variant: AvatarVariant.LARGE,
}))`
  position: relative;
  box-sizing: content-box;
`;

export const MemberStatusIcon = styled.div`
  width: 0.5rem;
  height: 0.5rem;
  position: absolute;
  bottom: -0.063rem;
  right: -0.125rem;
  box-sizing: content-box;
  z-index: ${ZIndexLayers.overlay};
  border-radius: 2.75rem;
  border: 1.5px solid ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
  background: ${(props: ThemeProp) => props.theme.colors.system.success};
`;

export const MemberChatIconContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  position: relative;
`; // Includes user image and status for Member header

export const GroupChatIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 1rem;
  background-color: ${(props: ThemeProp) => props.theme.colors.primary.c200};
`; // Includes group image

export const ChatHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: ${(props: { theme: { breakpoints: { mobile: any } } }) => props.theme.breakpoints.mobile}) {
    max-width: 60%;
  }
`;

export const GroupChatHeaderInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.313rem;
`;

export const ChatHeaderInfoTitle = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body1.fontSize};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body1.lineHeight};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const ChatHeaderInfoSubTitle = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.fontSize};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.lineHeight};
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
`;
interface GroupChatHeaderIconContainerProps {
  $isExpanded?: boolean;
}
export const GroupChatHeaderIconContainer = styled.div<GroupChatHeaderIconContainerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  transform: ${(props: { $isExpanded: any }) => (props.$isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s;
  cursor: pointer;
`;

export const CloseButtonContainer = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  cursor: pointer;

  svg {
    stroke: ${(props: ThemeProp) => props.theme.colors.white};
  }

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    svg {
      stroke: ${(props: ThemeProp) => props.theme.colors.primary.c300};
    }
  }
`;

interface ChatBodyProps {
  $hasMoreMessages?: boolean;
  $hideScroll?: boolean;
}
export const ChatBody = styled.div<ChatBodyProps>`
  display: block;
  height: 100%;
  width: 100%;
  overflow-y: scroll;
  position: relative;
  padding: ${(props: { $hasMoreMessages: any }) =>
    props.$hasMoreMessages ? '1rem 1.25rem 1.25rem 1.25rem' : '1.25rem'};
  overflow: ${(props: { $hideScroll: any }) => (props.$hideScroll ? 'hidden' : 'auto')};
`;

export const ChatMessagesContainer = styled.div<any>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${(props: { centerContent: any }) => (props.centerContent ? 'center' : 'flex-end')};
  flex: 1;
  margin: 1rem 1.5rem;
  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
`;

export const ChatBodyEmptyPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  max-width: 20.125rem;
`;

export const ChatEmptyInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
  text-align: center;
`;

export const ChatBodyEmptyPlaceholderTitle = styled(Text)`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.mlg};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.m};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c900};
`;

export const ChatBodyEmptyPlaceholderSubtitle = styled(Text)`
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body2.fontSize};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body2.lineHeight};
  color: ${(props: ThemeProp) => props.theme.colors.content.subdued.high};
  max-width: 19rem;
`;

export const ChatInputBox = styled.div`
  display: flex;
  align-items: flex-end;
  width: 100%;
`;

export const ChatImagePreviewContainer = styled.div`
  display: flex;
  max-width: 4rem;
  align-items: center;
  position: relative;

  #close-icon {
    width: 0.875rem;
    height: 0.875rem;
    box-sizing: content-box;
    border-radius: 1.25rem;
    border: 0.7px solid ${(props: ThemeProp) => props.theme.colors.black};
    stroke: ${(props: ThemeProp) => props.theme.colors.white};
    background: ${(props: ThemeProp) => props.theme.colors.black};
    cursor: pointer;

    position: absolute;
    top: 0.125rem;
    right: 0.563rem;
  }
`;

export const ChatImageLoading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  width: 2.5rem;
  height: 2.5rem;
  margin: 0.5rem;
  border-radius: 0.25rem;
  background: ${(props: ThemeProp) => props.theme.colors.greyscale.c300};
  position: relative;
`;

export const ChatPDFLoading = styled(ChatImageLoading)``;

export const PdfIconContainer = styled.div`
  top: 0.125rem;
  position: absolute;
  overflow: hidden;
  background: transparent;
  border-radius: 0.25rem;
`;

export const StyledFileIcon = styled(FileIcon)`
  margin-top: 0.25rem;
  width: 1.25rem;
  height: 1.25rem;
  color: ${(props: ThemeProp) => props.theme.colors.main.c200};
`;

export const StyledCSVFileIcon = styled(CSVFileIcon)`
  width: 2rem;
  height: 2rem;
  color: ${(props: ThemeProp) => props.theme.colors.main.c200};
`;

export const StyledPDFFileIcon = styled(PDFFileIcon)`
  width: 2rem;
  height: 2rem;
  color: ${(props: ThemeProp) => props.theme.colors.main.c200};
`;

export const ChatImageProgressBar = styled.div`
  width: 2.25rem;
  height: 0.25rem;
  display: flex;

  background: ${(props: ThemeProp) => props.theme.colors.white};
  border-radius: 0.188rem;
  margin: 0.188rem 0.125rem;
  padding: 0.063rem;
`;

interface ChatImageProgressBarFilledProps {
  percentage: number;
}
export const ChatImageProgressBarFilled = styled.div<ChatImageProgressBarFilledProps>`
  border-radius: 0.188rem;
  background: ${(props: ThemeProp) => props.theme.colors.primary.c600};
  width: ${(props: { percentage: any }) => props.percentage}%;
`;

export const ChatImagePreview = styled.img`
  position: relative;
  width: 2.5rem;
  height: 2.5rem;
  margin: 0.5rem;
  border-radius: 0.25rem;
  object-fit: cover;

  background: ${(props: ThemeProp) => props.theme.colors.greyscale.c300};
`;

export const ChatInputButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-left: 0.5rem;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

export const ActionButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: transparent;
  cursor: pointer;
  width: 2rem;
  height: 2rem;
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c500};
  background: ${(props: ThemeProp) => props.theme.colors.layout.surface.default};
  border-radius: 50%;
  justify-self: flex-end;
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c200};

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    background: transparent;
  }
`;

export const MobileActionButton = styled(ActionButton)`
  display: none;
  margin-right: 0.5rem;
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  margin-left: 0;

  svg {
    width: 1.5rem;
    height: 1.5rem;
    path {
      stroke: ${(props: ThemeProp) => props.theme.colors.main.c600};
    }
  }
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: flex;
  }
`;

export const SendButton = styled(Button)`
  margin-left: auto;
  min-width: 3.75rem;
  width: 3.75rem;
  height: 100%;
  padding: 0.25rem 1.063rem;
  height: 2rem;
`;

export const StyledMessageBubble = styled(ChatMessageBubble)`
  margin-bottom: 1rem;
`;

export const MessagesContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

export const EmptyChatView = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

export const StyledInfiniteScroll = styled(InfinitelyScrolled)`
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-self: flex-end;
  display: inline-flex;
  align-self: flex-end;
  justify-content: flex-end;
`;

/**
 * Expanded Group Header
 */
export const ExpandedChatHeaderAllMembersContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const ExpandedChatHeaderAllMembersText = styled.p`
  margin-left: 0.5rem;
  font-size: ${(props: { theme: { fontSize: { s: any } } }) => props.theme.fontSize.s};
  line-height: ${(props: { theme: { lineHeight: { m: any } } }) => props.theme.lineHeight.m};
  font-weight: ${(props: { theme: { fontWeight: { regular: any } } }) => props.theme.fontWeight.regular};
`;

export const AllMembersListContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: baseline;
  padding: 0.5rem;
  min-width: 11.25rem;
  max-height: 21.875rem;
  overflow: scroll;
`;

export const MembersTooltip = styled(Tooltip).attrs((props) => ({
  css: {
    backgroundColor: `${props.theme.colors.white}`,
    border: `solid 1px ${props.theme.colors.greyscale.c200}`,
    minWidth: '11.25rem',
    zIndex: 999999,
  },
}))``;

export const StyledChatIcon = styled(ChatIcon).attrs((props) => {
  return {
    color: props.theme.colors.primary.c700,
  };
})``;

export const StyledChevronIcon = styled(Chevron2Icon)`
  stroke: ${(props: { theme: { colors: { white: any } } }) => props.theme.colors.white};
`;

export const StyledVehicleChatPlaceholder = styled(VehicleChatPlaceholder)`
  width: 2.5rem;
  height: 2.5rem;
`;

export const BackButtonContainer = styled.div`
  display: none;
  align-items: center;
  justify-content: center;
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    display: flex;
  }
`;

export const StyledBackArrowIcon = styled(BackArrowIcon)`
  width: 0.9rem;
  height: 0.9rem;
  flex-shrink: 0;

  path {
    stroke-width: 1.5px;
    stroke: ${(props: ThemeProp) => props.theme.colors.neutrals.c900};
  }
`;

export const ConversationTopViewContainer = styled.div`
  background: ${(props: ThemeProp) => props.theme.colors.greyscale.c800};
  padding: 0.75rem 1.5rem;
`;

export const ConversationDateDivider = styled.div`
  display: flex;
  padding: 0.125rem 0.375rem;
  margin-bottom: 1rem;
  align-self: center;
  color: ${(props: { theme: { colors: { content: { subdued: { high: any } } } } }) =>
    props.theme.colors.content.subdued.high};
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.mobile.caption1.fontSize};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.mobile.caption1.lineHeight};
  letter-spacing: 0.5px;
  text-transform: uppercase;
  z-index: ${ZIndexLayers.fixedElement};
  border-radius: 6px;
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.layout.divider};
  background: ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
  box-shadow: 0px 5px 30px 0px rgba(0, 0, 0, 0.1), 0px 1px 2px 0px rgba(118, 113, 122, 0.4); // NOTE: Spec from design system.
  position: sticky;
  top: 0px;
  align-self: center;
`;

export const MessagesDateBlock = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
  justify-content: flex-end;
`;

export const StyledForm = styled.form`
  flex: 1;
`;

interface MobileSendButtonProps {
  $visible?: boolean;
}

export const MobileSendButton = styled(IconButton)<MobileSendButtonProps>`
  display: none;
  margin: 0;
  padding: 0;
  width: 2.5rem;
  height: 2.5rem;
  cursor: pointer;
  border-radius: 0.25rem;
  background: ${(props: ThemeProp) => props.theme.colors.main.c600};
  margin-left: 0.5rem;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: ${(props: MobileSendButtonProps) => (props.$visible ? 'flex' : 'none')};
  }
`;

export const FooterContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: ${(props: ThemeProp) => props.theme.colors.neutrals.c050};

  border-top: 1px solid ${(props: ThemeProp) => props.theme.colors.layout.divider};
  padding: 0.8125rem 1rem 0.75rem 1rem;
  z-index: ${ZIndexLayers.immediatelyAboveFixedElement + 1}; //One more than mentions bottom sheet

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    padding: 0.75rem 1rem 0.75rem 1rem;
    background: ${(props: ThemeProp) => props.theme.colors.layout.surface.default};
  }
`;
interface NewMessagesPillProps {
  $visible: boolean;
}

export const NewMessagesPill = styled.div<NewMessagesPillProps>`
  position: absolute;
  left: 50%;
  top: -2.5rem;
  transform: translate(-50%, 0);
  display: ${(props: NewMessagesPillProps) => (props.$visible ? 'inline-flex' : 'none')};
  padding: 0.25rem 0.375rem 0.25rem 0.5rem;
  justify-content: center;
  align-items: center;
  border-radius: 0.375rem;
  box-shadow: ${(props: ThemeProp) => props.theme.shadows.pill};
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.layout.divider};
  background: ${(props: ThemeProp) => props.theme.colors.layout.surface.default};
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.fontSize};
  font-style: normal;
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  letter-spacing: 0.03125rem;
  text-transform: uppercase;
  color: ${(props: ThemeProp) => props.theme.colors.main.c600};
  z-index: ${ZIndexLayers.overlay};
  transition: opacity 0.25s ease-in;
  cursor: pointer;
`;

export const MessagesShimmerContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
  height: 100%;
`;

export const MobileShimmerText = styled(ShimmerText)`
  display: none;
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: flex;
  }
`;

export const InputShimmer = styled(ShimmerText)`
  && {
    &.shimmer-title {
      margin-left: 0 !important;
    }
    & .shimmer-title-line {
      border-radius: 2.125rem;
      height: 2rem;
    }
    @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
      &.shimmer-title {
        margin-left: 0.5rem !important;
      }
      & .shimmer-title-line {
        border-radius: 0.25rem;
        height: 2.5rem;
      }
    }
  }
`;

export const MentionsBottomSheet = styled(motion.div)`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 60px;
  width: 100%;
  height: 50%;
  margin: 0;
  background: ${(props: ThemeProp) => props.theme.colors.layout.surface.default};
  box-shadow: ${(props: ThemeProp) => props.theme.shadows.menu};
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.layout.divider};
  overflow-y: auto;
  z-index: ${ZIndexLayers.immediatelyAboveFixedElement};
  -webkit-overflow-scrolling: touch;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    position: fixed;
    bottom: 0;
    max-height: 35vh;
    border-bottom: none;
    border-radius: 1rem 1rem 0 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain; // Prevent scroll chaining
  }
`;

export const MentionItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${(props: ThemeProp) => props.theme.colors.main.c050};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${(props: ThemeProp) => props.theme.colors.layout.divider};
  }
`;

export const MentionAvatar = styled(Avatar)`
  width: 2rem;
  height: 2rem;
  margin-right: 0.75rem;
  flex-shrink: 0;
`;

export const MentionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  overflow: hidden;
`;

export const MentionName = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body2.fontSize};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body2.lineHeight};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  color: ${(props: ThemeProp) => props.theme.colors.content.default};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const MentionEmail = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.fontSize};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.lineHeight};
  color: ${(props: ThemeProp) => props.theme.colors.content.subdued.high};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const NoMentionsFound = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body1.fontSize};
  padding: 1rem;
  color: ${(props: ThemeProp) => props.theme.colors.content.subdued.high};
`;

export const DraftWrapper = styled.div<{ $isMinHeight?: boolean; $height?: number }>`
  width: 100%;
  display: flex;
  flex-direction: column-reverse;
  height: ${(props) => props.$height}px;
  transition: height 0.2s ease-out;
  position: relative;
  max-height: 7.25rem; // Equivalent to 3 rows
  .DraftEditor-root {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: auto;
    min-height: 2.125rem; // 34px
    max-height: 7.25rem; // Equivalent to 3 rows
    overflow-y: auto;
    font-family: inherit;
    border-radius: ${(props: { $isMinHeight: boolean }) => (props.$isMinHeight ? '3rem' : '0.8125rem')};
    font-size: 0.875rem;
    line-height: 1.25rem;
    padding: 0.375rem 1rem;
    background: white;
    border: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c200};
    resize: none;
    color: black;
    word-wrap: break-word;
    word-break: break-word;

    &:focus {
      outline: none;
    }

    &::placeholder {
      color: grey;
    }

    @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
      min-height: 2.5rem;
      padding: 0.5rem 1rem;
      border-radius: 0.25rem;
      border: none;
      background: ${(props: ThemeProp) => props.theme.colors.neutrals.c050};
    }
  }

  .DraftEditor-editorContainer {
    position: relative;
    z-index: 1;
    width: 100%;
  }

  .public-DraftEditorPlaceholder-root {
    color: ${({ theme }) => theme.colors.gray500};
    position: absolute;
    z-index: 0;
    padding: 0;
    margin: 0;
    width: calc(100% - 2rem);
    overflow: hidden;
  }

  .public-DraftEditorPlaceholder-inner {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    width: 100%;
  }

  .public-DraftEditor-content {
    min-height: 1.25rem;
    width: 100%;
    white-space: pre-wrap;
    word-wrap: break-word;
    word-break: break-word;

    & > div {
      margin: 0;
      padding: 0;
    }

    [data-contents='true'] {
      margin: 0;
      padding: 0;
      width: 100%;
    }

    .public-DraftStyleDefault-block {
      margin: 0;
      padding: 0;
      width: 100%;
      white-space: pre-wrap;
      word-wrap: break-word;
      word-break: break-word;
    }
  }

  .mention {
    color: ${({ theme }) => theme.colors.main.c600};
    font-weight: 500;
    text-decoration: none;
  }
`;

export const StyledEditor = styled(Editor)`
  width: 100%;
`;

export const DefaultMentionUserImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: 2rem;
  min-width: 2rem;
  height: 2rem;
  min-height: 2rem;
  border-radius: 50%;
  margin-right: 0.75rem;
`;

export const PdfPreviewPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: ${(props) => props.theme.colors.content.white};
  border-radius: 0.5rem;
  padding: 1rem;
  gap: 0.5rem;

  svg {
    width: 1.5rem;
    height: 1.5rem;
    color: ${(props) => props.theme.colors.content.default};
  }

  span {
    font-size: 0.875rem;
    color: ${(props) => props.theme.colors.content.default};
    font-weight: 500;
  }
`;
